// Historie naposledy otevřených agend (posledních 15), perzistentní v localStorage (přežije reload).
//
// Neutrální leaf util (žádná závislost na windowing/legacyMagicBridge) — zapisuje ho jak bridge
// (legacy form/open), tak menu/historie UI; čte ho taskbar. Dedup per `id`; nejnovější první.

import { create } from 'zustand';

const STORAGE_KEY = 'phx:agenda-history:v1';
const MAX_ITEMS = 15;

export type AgendaHistoryKind = 'legacy' | 'native';

export interface AgendaHistoryItem {
  /** Dedup klíč: `legacy:<GlobalName>` nebo `native:<path>`. */
  readonly id: string;
  readonly kind: AgendaHistoryKind;
  /** legacy: Magic Form GlobalName (např. `Person.List`); native: route path. */
  readonly target: string;
  readonly title: string;
  /** Parametry pro znovuotevření (legacy formParams). */
  readonly params?: Record<string, unknown>;
  /** Čas posledního otevření (ms). */
  readonly at: number;
}

/** Vstup pro zápis — `id`/`at` se dopočítají. */
export type AgendaHistoryInput = Omit<AgendaHistoryItem, 'id' | 'at'> & { readonly id?: string };

interface AgendaHistoryState {
  readonly items: readonly AgendaHistoryItem[];
  /** Zapíše/posune položku na začátek; vrátí její `id`. */
  readonly record: (input: AgendaHistoryInput) => string;
  /** Přepíše titulek položky (in-place, bez posunu) — doplnění titulku okna po načtení. */
  readonly setTitle: (id: string, title: string) => void;
  readonly clear: () => void;
}

/** Stabilní serializace parametrů (seřazené klíče) — pro identitu položky historie. */
function stableParams(params?: Record<string, unknown>): string {
  if (!params) return '';
  const keys = Object.keys(params).filter((k) => params[k] !== undefined && params[k] !== null);
  if (keys.length === 0) return '';
  keys.sort();
  return keys.map((k) => `${k}=${JSON.stringify(params[k])}`).join('&');
}

/** Identita položky = kind + klíč formuláře / id agendy + VŠECHNY parametry. */
function makeId(kind: AgendaHistoryKind, target: string, params?: Record<string, unknown>): string {
  const p = stableParams(params);
  return p ? `${kind}:${target}?${p}` : `${kind}:${target}`;
}

function load(): AgendaHistoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (i): i is AgendaHistoryItem =>
          !!i &&
          typeof (i as AgendaHistoryItem).id === 'string' &&
          typeof (i as AgendaHistoryItem).target === 'string' &&
          typeof (i as AgendaHistoryItem).title === 'string'
      )
      .slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

function persist(items: readonly AgendaHistoryItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* localStorage nedostupné / plné — ignorujeme */
  }
}

export const useAgendaHistoryStore = create<AgendaHistoryState>((set, get) => ({
  items: load(),
  record: (input) => {
    const id = input.id ?? makeId(input.kind, input.target, input.params);
    const item: AgendaHistoryItem = {
      id,
      kind: input.kind,
      target: input.target,
      title: input.title,
      params: input.params,
      at: Date.now(),
    };
    // Dedup: stejnou agendu odstraň a vlož na začátek (move-to-top), ořež na MAX_ITEMS.
    const next = [item, ...get().items.filter((i) => i.id !== id)].slice(0, MAX_ITEMS);
    persist(next);
    set({ items: next });
    return id;
  },
  setTitle: (id, title) =>
    set((s) => {
      const idx = s.items.findIndex((i) => i.id === id);
      if (idx < 0 || s.items[idx].title === title) return s;
      const next = s.items.slice();
      next[idx] = { ...next[idx], title } as AgendaHistoryItem;
      persist(next);
      return { items: next };
    }),
  clear: () => {
    persist([]);
    set({ items: [] });
  },
}));

/** Imperativní zápis mimo React (z bridge message listeneru). Vrací `id` zapsané položky. */
export function recordAgendaHistory(input: AgendaHistoryInput): string {
  return useAgendaHistoryStore.getState().record(input);
}

// Mapa layerId → historyId: po otevření okna ji naplní zapisovatel (bridge / historie), aby se po
// načtení okna (window/chrome) dal doplnit jeho TITULEK k odpovídajícímu záznamu historie.
const layerToHistoryId = new Map<string, string>();

/** Propojí otevřené okno (layerId) se záznamem historie (historyId). */
export function linkLayerToHistory(layerId: string, historyId: string): void {
  layerToHistoryId.set(layerId, historyId);
}

/** Doplní titulek okna do navázaného záznamu historie (po načtení, když je znám titulek). */
export function updateAgendaHistoryTitleForLayer(layerId: string, title: string): void {
  if (!title) return;
  const id = layerToHistoryId.get(layerId);
  if (id) useAgendaHistoryStore.getState().setTitle(id, title);
}
